import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  clientApiService,
  TokenPackage,
  TokenTransactionItem,
} from "../../services/client-api.service";

function formatCurrency(n: number) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
  }).format(n);
}

function formatDate(t: number) {
  return new Date(t).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const TYPE_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  purchase: { label: "Compra", icon: "💰", color: "text-green-600" },
  usage: { label: "Uso", icon: "🎨", color: "text-red-600" },
  refund: { label: "Reembolso", icon: "↩️", color: "text-yellow-600" },
  bonus: { label: "Bonus", icon: "🎁", color: "text-blue-600" },
};

export function TokenWalletPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [balance, setBalance] = useState<number>(0);
  const [packages, setPackages] = useState<TokenPackage[]>([]);
  const [transactions, setTransactions] = useState<TokenTransactionItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [selectedPackage, setSelectedPackage] = useState<TokenPackage | null>(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoResult, setPromoResult] = useState<{
    valid: boolean;
    bonusTokens?: number;
    description?: string | null;
    reason?: string;
  } | null>(null);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [balanceData, packagesData, transactionsData] = await Promise.all([
        clientApiService.getTokenBalance(),
        clientApiService.listTokenPackages(),
        clientApiService.listTokenTransactions(100),
      ]);
      setBalance(balanceData.balance);
      setPackages(packagesData);
      setTransactions(transactionsData);
    } catch (err: any) {
      setError(err?.message || "Error al cargar monedero");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Manejo de retorno de Stripe
  useEffect(() => {
    const status = searchParams.get("status");
    if (status === "success") {
      setSuccessMessage(
        "¡Pago recibido! Tus tokens se acreditarán automáticamente en los próximos segundos."
      );
      // Refrescar después de unos segundos para ver los tokens
      setTimeout(load, 3000);
      // Limpiar query params
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("status");
      newParams.delete("session_id");
      setSearchParams(newParams, { replace: true });
    } else if (status === "cancel") {
      setError("Pago cancelado. No se realizó ningún cargo.");
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("status");
      setSearchParams(newParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return;
    setIsValidatingPromo(true);
    try {
      const result = await clientApiService.validatePromo(promoCode.trim());
      setPromoResult(result);
    } catch (err: any) {
      setPromoResult({ valid: false, reason: err?.message || "Error" });
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const handleBuy = async () => {
    if (!selectedPackage) return;
    setIsCreatingCheckout(true);
    setError(null);

    try {
      const result = await clientApiService.createTokenCheckout({
        packageId: selectedPackage.id,
        promoCode: promoResult?.valid ? promoCode.trim() : undefined,
      });
      window.location.href = result.url;
    } catch (err: any) {
      setError(err?.message || "Error al procesar la compra");
      setIsCreatingCheckout(false);
    }
  };

  const openBuyModal = (pkg: TokenPackage) => {
    setSelectedPackage(pkg);
    setPromoCode("");
    setPromoResult(null);
  };

  const closeBuyModal = () => {
    if (isCreatingCheckout) return;
    setSelectedPackage(null);
    setPromoCode("");
    setPromoResult(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const finalBonus = promoResult?.valid ? promoResult.bonusTokens ?? 0 : 0;
  const totalTokens = selectedPackage ? selectedPackage.tokens + finalBonus : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Monedero de Tokens</h2>
          <p className="text-sm text-slate-500 mt-1">
            Compra tokens para usar servicios de IA
          </p>
        </div>
        <button
          onClick={load}
          disabled={isLoading}
          className="text-sm bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 px-4 py-2 rounded-lg font-medium transition disabled:opacity-50"
        >
          🔄 Actualizar
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl p-4">
          {successMessage}
        </div>
      )}

      {/* Balance */}
      <div className="bg-gradient-to-r from-primary to-primary-dark text-white rounded-2xl p-6">
        <p className="text-white/80 text-sm">Saldo disponible</p>
        <div className="flex items-baseline gap-3 mt-1">
          <span className="text-5xl font-bold">{balance}</span>
          <span className="text-white/80">tokens</span>
        </div>
        <p className="text-white/70 text-xs mt-2">
          Cada token te da acceso a un servicio de IA. No expiran.
        </p>
      </div>

      {/* Paquetes */}
      <div>
        <h3 className="text-lg font-bold text-slate-900 mb-3">
          Paquetes disponibles
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`relative bg-white rounded-2xl border-2 p-5 flex flex-col ${
                pkg.popular ? "border-primary" : "border-slate-200"
              }`}
            >
              {pkg.popular && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-bold px-3 py-0.5 rounded-full">
                  POPULAR
                </span>
              )}
              <div className="text-center mb-4">
                <p className="text-3xl font-bold text-primary">{pkg.tokens}</p>
                <p className="text-xs text-slate-500">tokens</p>
              </div>
              <p className="text-center text-lg font-bold text-slate-900">
                {formatCurrency(pkg.price)}
              </p>
              {pkg.savings > 0 && (
                <p className="text-center text-xs text-green-600 font-medium mt-1">
                  Ahorras {pkg.savings}%
                </p>
              )}
              <p className="text-center text-xs text-slate-500 mt-2 mb-4">
                {pkg.name}
                {pkg.description && ` • ${pkg.description}`}
              </p>
              <button
                onClick={() => openBuyModal(pkg)}
                className="mt-auto w-full py-2.5 bg-primary hover:bg-primary-dark text-white rounded-lg font-semibold transition"
              >
                Comprar
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Info adicional */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
          <h4 className="font-bold text-blue-900 mb-2">💡 ¿Cómo usar tus tokens?</h4>
          <p className="text-sm text-blue-700">
            Los tokens se consumen automáticamente al utilizar servicios de IA
            como el generador de modelos 3D. Cada servicio tiene un costo
            específico en tokens.
          </p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4">
          <h4 className="font-bold text-purple-900 mb-2">🎁 Descuento en contrato</h4>
          <p className="text-sm text-purple-700">
            Si concretas tu proyecto con nosotros, el costo de los tokens que
            usaste se descuenta del contrato final de construcción.
          </p>
        </div>
      </div>

      {/* Historial */}
      <div className="bg-white rounded-2xl border border-slate-200">
        <div className="p-5 border-b border-slate-100">
          <h3 className="font-bold text-slate-900">Historial de transacciones</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {transactions.length} movimientos
          </p>
        </div>
        {transactions.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-4xl mb-2 opacity-40">📭</div>
            <p className="text-sm text-slate-500">Sin transacciones aún</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {transactions.map((tx) => {
              const info = TYPE_LABELS[tx.type] || {
                label: tx.type,
                icon: "💳",
                color: "text-slate-600",
              };
              return (
                <li key={tx.id} className="flex items-center gap-3 p-4">
                  <span className="text-2xl">{info.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-slate-900">
                      {info.label}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {tx.description}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {formatDate(tx.createdAt)}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-lg font-bold ${info.color}`}>
                      {tx.amount > 0 ? "+" : ""}
                      {tx.amount}
                    </p>
                    <p className="text-xs text-slate-400">tokens</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Buy modal */}
      {selectedPackage && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={closeBuyModal}
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">
                Confirmar compra
              </h3>
              <button
                onClick={closeBuyModal}
                disabled={isCreatingCheckout}
                className="text-slate-400 hover:text-slate-700 text-xl disabled:opacity-50"
              >
                ×
              </button>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-600">Paquete</span>
                <span className="font-semibold text-slate-900">
                  {selectedPackage.name}
                </span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-600">Tokens base</span>
                <span className="font-semibold text-slate-900">
                  {selectedPackage.tokens}
                </span>
              </div>
              {finalBonus > 0 && (
                <div className="flex items-center justify-between mb-2 text-green-600">
                  <span>Bonus promo</span>
                  <span className="font-semibold">+{finalBonus}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                <span className="font-semibold text-slate-900">Total tokens</span>
                <span className="font-bold text-primary text-lg">
                  {totalTokens}
                </span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="font-semibold text-slate-900">Total a pagar</span>
                <span className="font-bold text-slate-900 text-lg">
                  {formatCurrency(selectedPackage.price)}
                </span>
              </div>
            </div>

            {/* Promo */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Código promocional (opcional)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value.toUpperCase());
                    setPromoResult(null);
                  }}
                  placeholder="Ej: BIENVENIDO10"
                  maxLength={30}
                  disabled={isValidatingPromo || isCreatingCheckout}
                  className="flex-1 px-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-primary outline-none text-sm uppercase"
                />
                <button
                  onClick={handleValidatePromo}
                  disabled={!promoCode.trim() || isValidatingPromo || isCreatingCheckout}
                  className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium text-sm transition disabled:opacity-50"
                >
                  {isValidatingPromo ? "..." : "Aplicar"}
                </button>
              </div>
              {promoResult && (
                <p
                  className={`text-xs mt-1.5 ${
                    promoResult.valid ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {promoResult.valid
                    ? `✅ Código válido: +${promoResult.bonusTokens} tokens bonus${
                        promoResult.description ? ` — ${promoResult.description}` : ""
                      }`
                    : `❌ ${promoResult.reason}`}
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={closeBuyModal}
                disabled={isCreatingCheckout}
                className="flex-1 py-2.5 border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50 transition disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleBuy}
                disabled={isCreatingCheckout}
                className="flex-1 py-2.5 bg-primary hover:bg-primary-dark text-white rounded-lg font-semibold transition disabled:opacity-50"
              >
                {isCreatingCheckout ? "Procesando..." : "Pagar con tarjeta"}
              </button>
            </div>
            <p className="text-xs text-slate-400 text-center mt-3">
              Serás redirigido a Stripe para completar el pago
            </p>
          </div>
        </div>
      )}
    </div>
  );
}